type CustomLink = {
  position: 'before' | 'after';
  title: string;
  link: string;
  translations?: Record<string, string>;
};

export type Env = {
  APP_VERSION: string;
  APP_API_BASE_URL: string;
  APP_CONFIG_API_BASE_URL: string;
  APP_CONTENT_API_BASE_URL: string;
  APP_ACCOUNT_API_BASE_URL: string;
  APP_PLAYER_ID: string;
  APP_FOOTER_TEXT: string;
  APP_DEFAULT_LANGUAGE: string;
  APP_PUBLIC_URL: string;

  APP_CONSENT_COOKIE_NAME?: string;
  APP_CONSENT_COOKIE_POLICY_URL?: string;

  APP_DEFAULT_CONFIG_SOURCE?: string;
  APP_PLAYER_LICENSE_KEY?: string;

  APP_BODY_FONT?: string;
  APP_BODY_ALT_FONT?: string;

  APP_GTM_TAG_ID?: string;
  APP_GTM_TAG_SERVER?: string;
  APP_GTM_SCRIPT?: string;
  APP_GTM_LOAD_ON_ACCEPT: boolean;
  APP_GTM_COOKIE_BANNER: boolean;

  APP_CHROMECAST_APPLICATION_ID?: string;
  APP_DELETE_ACCOUNT_URL?: string;
  APP_TERMS_AND_CONDITIONS_URL?: string;
  APP_EXTERNAL_LOGIN_PROVIDERS?: string;

  APP_NAME?: string;
  APP_SHORT_NAME?: string;
  APP_DESCRIPTION?: string;

  APP_EPG_DAY_ROLLOVER_HOUR?: string;
  APP_EPG_VISIBLE_HOURS?: string;
  APP_MEDIA_PLAYLIST_SOURCE: 'feed' | 'recommendations';
  APP_CUSTOM_USER_LINKS?: CustomLink[];
  APP_HERO_SHELF_AUTO_SLIDE_INTERVAL?: string;
};

const env: Env = {
  APP_VERSION: '',
  APP_API_BASE_URL: 'https://cdn.jwplayer.com',
  APP_CONFIG_API_BASE_URL: 'https://cdn.jwplayer.com',
  APP_CONTENT_API_BASE_URL: 'https://cdn.jwplayer.com',
  APP_ACCOUNT_API_BASE_URL: 'https://cdn.jwplayer.com',
  APP_PLAYER_ID: 'M4qoGvUk',
  APP_FOOTER_TEXT: '',
  APP_DEFAULT_LANGUAGE: 'en',
  APP_PUBLIC_URL: '',
  APP_GTM_LOAD_ON_ACCEPT: false,
  APP_GTM_COOKIE_BANNER: true,
  APP_DELETE_ACCOUNT_URL: '',
  APP_MEDIA_PLAYLIST_SOURCE: 'recommendations',
};

function coerceBooleanOption(value: unknown): boolean | undefined {
  if (typeof value === 'string') {
    return value === 'true';
  }
  if (typeof value === 'boolean') {
    return value;
  }
  return undefined;
}

function coerceJsonOption<T>(value: unknown): T | undefined {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

export const configureEnv = (options: Partial<Env>) => {
  // @TODO should we loop over each option instead?
  env.APP_VERSION = options.APP_VERSION || env.APP_VERSION;
  env.APP_API_BASE_URL = options.APP_API_BASE_URL || env.APP_API_BASE_URL;
  env.APP_CONFIG_API_BASE_URL = options.APP_CONFIG_API_BASE_URL || env.APP_CONFIG_API_BASE_URL;
  env.APP_CONTENT_API_BASE_URL = options.APP_CONTENT_API_BASE_URL || env.APP_CONTENT_API_BASE_URL;
  env.APP_ACCOUNT_API_BASE_URL = options.APP_ACCOUNT_API_BASE_URL || env.APP_ACCOUNT_API_BASE_URL;
  env.APP_PLAYER_ID = options.APP_PLAYER_ID || env.APP_PLAYER_ID;
  env.APP_FOOTER_TEXT = options.APP_FOOTER_TEXT || env.APP_FOOTER_TEXT;
  env.APP_DEFAULT_LANGUAGE = options.APP_DEFAULT_LANGUAGE || env.APP_DEFAULT_LANGUAGE;
  env.APP_PUBLIC_URL = options.APP_PUBLIC_URL || env.APP_PUBLIC_URL;

  env.APP_CONSENT_COOKIE_NAME ||= options.APP_CONSENT_COOKIE_NAME;
  env.APP_CONSENT_COOKIE_POLICY_URL ||= options.APP_CONSENT_COOKIE_POLICY_URL;
  env.APP_DEFAULT_CONFIG_SOURCE ||= options.APP_DEFAULT_CONFIG_SOURCE;
  env.APP_PLAYER_LICENSE_KEY ||= options.APP_PLAYER_LICENSE_KEY;
  env.APP_EPG_VISIBLE_HOURS ||= options.APP_EPG_VISIBLE_HOURS;
  env.APP_EPG_DAY_ROLLOVER_HOUR ||= options.APP_EPG_DAY_ROLLOVER_HOUR;

  env.APP_BODY_FONT = options.APP_BODY_FONT || env.APP_BODY_FONT;
  env.APP_BODY_ALT_FONT = options.APP_BODY_ALT_FONT || env.APP_BODY_ALT_FONT;

  env.APP_GTM_TAG_ID ||= options.APP_GTM_TAG_ID;
  env.APP_GTM_TAG_SERVER ||= options.APP_GTM_TAG_SERVER;
  env.APP_GTM_SCRIPT ||= options.APP_GTM_SCRIPT;
  env.APP_GTM_LOAD_ON_ACCEPT = coerceBooleanOption(options.APP_GTM_LOAD_ON_ACCEPT) ?? env.APP_GTM_LOAD_ON_ACCEPT;
  env.APP_GTM_COOKIE_BANNER = coerceBooleanOption(options.APP_GTM_COOKIE_BANNER) ?? env.APP_GTM_COOKIE_BANNER;

  env.APP_CHROMECAST_APPLICATION_ID = options.APP_CHROMECAST_APPLICATION_ID || env.APP_CHROMECAST_APPLICATION_ID;
  env.APP_DELETE_ACCOUNT_URL = options.APP_DELETE_ACCOUNT_URL || env.APP_DELETE_ACCOUNT_URL;
  env.APP_TERMS_AND_CONDITIONS_URL = options.APP_TERMS_AND_CONDITIONS_URL || env.APP_TERMS_AND_CONDITIONS_URL;
  env.APP_EXTERNAL_LOGIN_PROVIDERS = options.APP_EXTERNAL_LOGIN_PROVIDERS || env.APP_EXTERNAL_LOGIN_PROVIDERS;

  env.APP_NAME = options.APP_NAME || env.APP_NAME;
  env.APP_SHORT_NAME = options.APP_SHORT_NAME || env.APP_SHORT_NAME;
  env.APP_DESCRIPTION = options.APP_DESCRIPTION || env.APP_DESCRIPTION;

  env.APP_MEDIA_PLAYLIST_SOURCE = options.APP_MEDIA_PLAYLIST_SOURCE || env.APP_MEDIA_PLAYLIST_SOURCE;
  env.APP_CUSTOM_USER_LINKS = coerceJsonOption<CustomLink[]>(options.APP_CUSTOM_USER_LINKS || env.APP_CUSTOM_USER_LINKS);
  env.APP_HERO_SHELF_AUTO_SLIDE_INTERVAL = options.APP_HERO_SHELF_AUTO_SLIDE_INTERVAL || env.APP_HERO_SHELF_AUTO_SLIDE_INTERVAL;
};

export default env;
